import * as fs from 'fs';
import * as path from 'path';
import { fileURLToPath } from 'url';
import { Logger, AgentMetadata } from './types.js';
import { SmartAgentMatcher } from './smartAgentMatcher.js';

// ES模块中获取包的根目录
const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

/**
 * 资源加载器
 * 统一从 npm 包内部加载 agents、standards、templates 等资源
 * 
 * 运行环境：
 * 1. 开发环境：node mcp-server/dist/index.js
 *    - __dirname = mcp-server/dist
 *    - packageRoot = mcp-server/
 * 
 * 2. npx 运行：npx mta-mcp
 *    - __dirname = ~/.npm/_npx/xxx/node_modules/mta-mcp/dist
 *    - packageRoot = ~/.npm/_npx/xxx/node_modules/mta-mcp/
 * 
 * 3. 全局安装：npm install -g mta-mcp && mta
 *    - __dirname = /usr/local/lib/node_modules/mta-mcp/dist
 *    - packageRoot = /usr/local/lib/node_modules/mta-mcp/
 * 
 * 目录结构（npm 包）：
 * mta-mcp/
 * ├── dist/          <- 编译后的 JS（入口点）
 * ├── bin/           <- CLI 入口
 * ├── agents/        <- Agent 定义文件
 * ├── standards/     <- 编码规范文件
 * ├── templates/     <- 代码模板
 * └── common/        <- 通用规范
 */
export class ResourceLoader {
    private packageRoot: string;
    private logger?: Logger;
    private initialized: boolean = false;

    constructor(logger?: Logger) {
        this.logger = logger;
        this.packageRoot = this.detectPackageRoot();
        this.validateResources();
    }

    /**
     * 检测包根目录
     * 支持多种运行环境
     */
    private detectPackageRoot(): string {
        const candidateRoots = [
            path.resolve(__dirname, '..'),
            path.resolve(__dirname, '..', '..'),
            path.resolve(__dirname, '..', '..', '..'),
            process.cwd(),
            path.resolve(process.cwd(), '..'),
        ];

        for (const candidate of candidateRoots) {
            const packageJsonPath = path.join(candidate, 'package.json');
            const hasResources = ['agents', 'standards'].every((dir) =>
                fs.existsSync(path.join(candidate, dir))
            );

            if (fs.existsSync(packageJsonPath)) {
                try {
                    const pkg = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
                    if (pkg.name === 'mta-mcp') {
                        this.log(`📦 检测到包根目录: ${candidate}`);
                        return candidate;
                    }
                } catch {
                    // 忽略解析错误，继续尝试其他候选路径
                }
            }

            if (hasResources) {
                this.log(`📦 通过资源目录检测到包根目录: ${candidate}`);
                return candidate;
            }
        }

        const fallback = path.resolve(__dirname, '..');
        this.log(`📦 使用默认包根目录: ${fallback}`);
        return fallback;
    }

    /**
     * 验证资源目录是否存在
     */
    private validateResources(): void {
        const requiredDirs = ['agents', 'standards'];
        const missingDirs: string[] = [];
        
        for (const dir of requiredDirs) {
            const dirPath = path.join(this.packageRoot, dir);
            if (!fs.existsSync(dirPath)) {
                missingDirs.push(dir);
            }
        }
        
        if (missingDirs.length > 0) {
            this.error(`⚠️ 以下资源目录缺失: ${missingDirs.join(', ')}`);
            this.error(`   包根目录: ${this.packageRoot}`);
            this.error(`   请确保 npm 包已正确安装或运行 npm run sync`);
        } else {
            this.initialized = true;
            this.log(`✅ 资源目录验证通过`);
        }
    }

    /**
     * 获取资源目录路径
     */
    getResourceDir(type: 'agents' | 'standards' | 'templates' | 'common' | 'troubleshooting'): string {
        return path.join(this.packageRoot, type);
    }

    /**
     * 加载所有 Agents
     */
    loadAllAgents(): AgentMetadata[] {
        const agentsDir = this.getResourceDir('agents');
        const agents: AgentMetadata[] = [];
        
        this.log(`📁 从包内加载 Agents: ${agentsDir}`);
        
        if (!fs.existsSync(agentsDir)) {
            this.error(`Agents 目录不存在: ${agentsDir}`);
            this.error(`包根目录: ${this.packageRoot}`);
            this.error(`当前 __dirname: ${__dirname}`);
            return agents;
        }

        const matcher = new SmartAgentMatcher(this.logger);
        const files = fs.readdirSync(agentsDir);
        
        for (const file of files) {
            if (file.endsWith('.agent.md')) {
                try {
                    const filePath = path.join(agentsDir, file);
                    const content = fs.readFileSync(filePath, 'utf-8');
                    const metadata = matcher.parseAgentMetadata(`agents/${file}`, content);
                    agents.push(metadata);
                    this.log(`✅ 加载 Agent: ${metadata.title}`);
                } catch (error) {
                    this.error(`解析 ${file} 失败: ${error}`);
                }
            }
        }

        this.log(`✅ 成功加载 ${agents.length} 个 Agents`);
        return agents;
    }

    /**
     * 加载单个 Agent
     */
    loadAgent(agentId: string): { content: string; metadata: AgentMetadata } | null {
        const agentsDir = this.getResourceDir('agents');
        const filePath = path.join(agentsDir, `${agentId}.agent.md`);
        
        if (!fs.existsSync(filePath)) {
            this.error(`Agent 不存在: ${agentId}`);
            return null;
        }

        try {
            const content = fs.readFileSync(filePath, 'utf-8');
            const matcher = new SmartAgentMatcher(this.logger);
            const metadata = matcher.parseAgentMetadata(`agents/${agentId}.agent.md`, content);
            return { content, metadata };
        } catch (error) {
            this.error(`加载 Agent ${agentId} 失败: ${error}`);
            return null;
        }
    }

    /**
     * 列出所有可用的 Agent ID
     */
    listAgentIds(): string[] {
        const agentsDir = this.getResourceDir('agents');
        
        if (!fs.existsSync(agentsDir)) {
            return [];
        }

        return fs.readdirSync(agentsDir)
            .filter(f => f.endsWith('.agent.md'))
            .map(f => f.replace('.agent.md', ''));
    }

    /**
     * 加载规范文件
     */
    loadStandard(standardPath: string): string | null {
        // 支持相对路径，如 "frameworks/vue3-composition.md"
        const standardsDir = this.getResourceDir('standards');
        const filePath = path.join(standardsDir, standardPath);
        
        if (!fs.existsSync(filePath)) {
            // 也尝试 common 目录
            const commonPath = path.join(this.getResourceDir('common'), standardPath);
            if (fs.existsSync(commonPath)) {
                return fs.readFileSync(commonPath, 'utf-8');
            }
            this.error(`规范文件不存在: ${standardPath}`);
            return null;
        }

        return fs.readFileSync(filePath, 'utf-8');
    }

    /**
     * 列出规范目录下的所有文件
     */
    listStandards(subDir?: string): string[] {
        const standardsDir = this.getResourceDir('standards');
        const targetDir = subDir ? path.join(standardsDir, subDir) : standardsDir;
        
        if (!fs.existsSync(targetDir)) {
            return [];
        }

        const results: string[] = [];
        this.walkDir(targetDir, standardsDir, results);
        return results;
    }

    /**
     * 加载模板
     */
    loadTemplate(templatePath: string): string | null {
        const templatesDir = this.getResourceDir('templates');
        const filePath = path.join(templatesDir, templatePath);
        
        if (!fs.existsSync(filePath)) {
            this.error(`模板文件不存在: ${templatePath}`);
            return null;
        }

        return fs.readFileSync(filePath, 'utf-8');
    }

    /**
     * 列出模板目录
     */
    listTemplates(subDir?: string): string[] {
        const templatesDir = this.getResourceDir('templates');
        const targetDir = subDir ? path.join(templatesDir, subDir) : templatesDir;
        
        if (!fs.existsSync(targetDir)) {
            return [];
        }

        return fs.readdirSync(targetDir);
    }

    /**
     * 检查资源是否存在
     */
    resourceExists(type: 'agents' | 'standards' | 'templates' | 'common', relativePath: string): boolean {
        const baseDir = this.getResourceDir(type);
        return fs.existsSync(path.join(baseDir, relativePath));
    }

    /**
     * 获取包版本
     */
    getPackageVersion(): string {
        try {
            const packageJsonPath = path.join(this.packageRoot, 'package.json');
            if (fs.existsSync(packageJsonPath)) {
                const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
                return packageJson.version || 'unknown';
            }
        } catch {
            // ignore
        }
        return 'unknown';
    }

    /**
     * 获取资源统计信息
     */
    getResourceStats(): {
        agents: number;
        standards: number;
        templates: number;
        version: string;
    } {
        return {
            agents: this.listAgentIds().length,
            standards: this.listStandards().length,
            templates: this.listTemplates().length,
            version: this.getPackageVersion()
        };
    }

    private walkDir(dir: string, baseDir: string, results: string[]): void {
        const files = fs.readdirSync(dir);
        for (const file of files) {
            const filePath = path.join(dir, file);
            const stat = fs.statSync(filePath);
            if (stat.isDirectory()) {
                this.walkDir(filePath, baseDir, results);
            } else if (file.endsWith('.md')) {
                results.push(path.relative(baseDir, filePath));
            }
        }
    }

    private log(message: string): void {
        this.logger?.log(message);
    }

    private error(message: string): void {
        this.logger?.error(message);
    }
}

// 单例实例
let resourceLoaderInstance: ResourceLoader | null = null;

export function getResourceLoader(logger?: Logger): ResourceLoader {
    if (!resourceLoaderInstance) {
        resourceLoaderInstance = new ResourceLoader(logger);
    }
    return resourceLoaderInstance;
}
